import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import Image from 'components/base/Image';
import { ProductCardProps } from 'components/common/ProductCard';
import Ratio from 'components/toolbox/croquis/core/Ratio';
import { soldout_dimmed } from 'components/toolbox/croquis/ProductCard/styles';
import { useFbkRouter } from 'hooks';
import { useTracker } from 'util/Tracker';

interface Props {
  product: ProductCardProps;
  index?: number;
  navigation: string;
}

const recent_product_ubl = {
  object_section: 'recently_view_carousel',
  object_type: 'catalog',
};

/**
 * 찜 > 최근 본 상품 아이템
 * @component
 */
const Item = ({ product, navigation, index = 0 }: Props) => {
  const tracker = useTracker();
  const { ref, inView: in_view } = useInView({ threshold: 0.5 });
  const { goPdpPage } = useFbkRouter();
  const { image_url, title, catalog_product_id, sales_status } = product;

  const product_ubl = useMemo(() => {
    return {
      ...recent_product_ubl,
      object_id: String(catalog_product_id),
      object_idx: index,
    };
  }, [catalog_product_id, index]);

  useEffect(() => {
    if (in_view) {
      tracker.addLog({
        ...product_ubl,
        navigation,
        category: 'impression',
      });
    }
  }, [navigation, in_view, product_ubl, tracker]);

  const handleProductCardClick = () => {
    tracker.addLog({
      ...product_ubl,
      navigation,
      category: 'click',
    });
    goPdpPage(catalog_product_id);
  };

  return (
    <SC.Container onClick={handleProductCardClick} ref={ref}>
      <SC.ImageWrap className={cx({ soldout: sales_status === 'SOLD_OUT' })}>
        <Ratio width={83} height={83}>
          <SC.Thumbnail>
            <Image src={image_url || ''} alt={title || ''} layout='fill' objectFit='cover' use_fade />
          </SC.Thumbnail>
        </Ratio>
      </SC.ImageWrap>
    </SC.Container>
  );
};

export default Item;

const SC = {
  Container: styled.li`
    flex: 0;
    cursor: pointer;
    margin-right: 4px;
    min-width: 83px;
  `,
  ImageWrap: styled.div`
    ${soldout_dimmed}

    > div {
      border-radius: 2px;
    }
  `,
  Thumbnail: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    border-radius: inherit;

    &:after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.03);
    }
  `,
};
